import {
  Box3,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Vector3
} from 'three';
import { Easing, Tween } from '@tweenjs/tween.js';
import { EngineContext } from './engine-context';

type DrawDirection = 'xp' | 'yp' | 'zp' | 'xm' | 'ym' | 'zm';

/**
 * Manages the draw-direction arrow helper and normal arrow helper.
 * Extracted from Engine to reduce class size.
 */
export class HelperManager {
  constructor(private ctx: EngineContext) {}

  drawDirectionVectorChange(direction?: DrawDirection): void {
    setTimeout(() => {
      const ddv = this.ctx.session.project.drawDirectionVector;

      if (direction) {
        ddv.xp = false;
        ddv.yp = false;
        ddv.zp = false;
        ddv.xm = false;
        ddv.ym = false;
        ddv.zm = false;
        ddv[direction] = true;

        if (ddv.xp && ddv.yp && ddv.zp && ddv.xm && ddv.ym && ddv.zm) {
          ddv['xp'] = true;
        }
      }

      if (!direction) {
        if (ddv.xp) direction = 'xp';
        else if (ddv.yp) direction = 'yp';
        else if (ddv.zp) direction = 'zp';
        else if (ddv.xm) direction = 'xm';
        else if (ddv.ym) direction = 'ym';
        else if (ddv.zm) direction = 'zm';
        else direction = 'xp';
      }

      const directions: Record<DrawDirection, Vector3> = {
        xp: new Vector3(1, 0, 0),
        yp: new Vector3(0, 1, 0),
        zp: new Vector3(0, 0, 1),
        xm: new Vector3(-1, 0, 0),
        ym: new Vector3(0, -1, 0),
        zm: new Vector3(0, 0, -1)
      };
      this.createAndAnimateArrow(this.ctx.objectInfo.center, directions[direction], this.ctx.objectInfo.box, 10);
      if (!this.ctx.helpersVisible) this.hideArrow();
    });
  }

  createAndAnimateArrow(startPosition: Vector3, direction: Vector3, boundingBox: Box3, distance: number): void {
    if (this.ctx.arrowHelper) this.disposeArrow();
    if (this.ctx.arrowHelperTween) this.ctx.arrowHelperTween.stop();

    const normalizedDirection = new Vector3().copy(direction).normalize();

    const maxDimension = Math.max(
      boundingBox.max.x - boundingBox.min.x,
      boundingBox.max.y - boundingBox.min.y,
      boundingBox.max.z - boundingBox.min.z
    );
    const arrowLength = maxDimension * 0.5;

    if (normalizedDirection.x !== 0) {
      startPosition.clone()[normalizedDirection.x > 0 ? 'sub' : 'add'](new Vector3(arrowLength / 2, 0, 0));
    }
    if (normalizedDirection.y !== 0) {
      startPosition.clone()[normalizedDirection.y > 0 ? 'sub' : 'add'](new Vector3(0, arrowLength / 2, 0));
    }
    if (normalizedDirection.z !== 0) {
      startPosition.clone()[normalizedDirection.z > 0 ? 'sub' : 'add'](new Vector3(0, 0, arrowLength / 2));
    }

    const arrowGroup = new Group();

    const arrowColor = 0xff0000;
    const arrowEmissive = 0x660000;

    const shaftBaseThickness = 1.0;
    const shaftTipThickness = 0.6;
    const shaftLength = arrowLength * 0.7;
    const shaftGeometry = new CylinderGeometry(shaftTipThickness, shaftBaseThickness, shaftLength, 24);
    const shaftMaterial = new MeshStandardMaterial({
      color: arrowColor,
      metalness: 0.7,
      roughness: 0.2,
      emissive: arrowEmissive,
      emissiveIntensity: 0.6,
      depthTest: false,
      alphaTest: 0
    });
    const shaftMesh = new Mesh(shaftGeometry, shaftMaterial);
    const shaftMidPoint = startPosition.clone().addScaledVector(normalizedDirection, shaftLength / 2);
    shaftMesh.position.copy(shaftMidPoint);
    shaftMesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), normalizedDirection);

    const headLength = arrowLength * 0.3;
    const headRadius = shaftBaseThickness * 2.5;
    const headGeometry = new ConeGeometry(headRadius, headLength, 24);
    const headMaterial = new MeshStandardMaterial({
      color: arrowColor,
      metalness: 0.7,
      roughness: 0.2,
      emissive: arrowEmissive,
      emissiveIntensity: 0.6,
      depthTest: false,
      alphaTest: 0
    });
    const headMesh = new Mesh(headGeometry, headMaterial);
    const headPosition = startPosition.clone().addScaledVector(normalizedDirection, shaftLength + headLength / 2);
    headMesh.position.copy(headPosition);
    headMesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), normalizedDirection);

    arrowGroup.add(shaftMesh);
    arrowGroup.add(headMesh);

    arrowGroup.children.forEach((child) => {
      child.renderOrder = 999;
    });

    this.ctx.scene.add(arrowGroup);
    this.ctx.arrowHelper = arrowGroup;

    const targetPosition = new Vector3().copy(startPosition).addScaledVector(normalizedDirection, distance);
    const animState = { x: startPosition.x, y: startPosition.y, z: startPosition.z, glow: 0.6 };
    const targetState = { x: targetPosition.x, y: targetPosition.y, z: targetPosition.z, glow: 1.2 };

    const tween = new Tween(animState)
      .to(targetState, 1200)
      .easing(Easing.Quadratic.InOut)
      .yoyo(true)
      .repeat(Infinity)
      .onUpdate(() => {
        const newPosition = new Vector3(animState.x, animState.y, animState.z);
        shaftMesh.position.copy(newPosition.clone().addScaledVector(normalizedDirection, shaftLength / 2));
        shaftMesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), normalizedDirection);
        headMesh.position.copy(newPosition.clone().addScaledVector(normalizedDirection, shaftLength + headLength / 2));
        headMesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), normalizedDirection);
        shaftMaterial.emissiveIntensity = animState.glow;
        headMaterial.emissiveIntensity = animState.glow;
      });

    this.ctx.arrowHelperTween = tween.start() as unknown as Tween<Record<string, number>>;
  }

  showArrow(): void {
    if (this.ctx.arrowHelper) {
      this.ctx.arrowHelper.visible = true;
    }
  }

  hideArrow(): void {
    if (this.ctx.arrowHelper) {
      this.ctx.arrowHelper.visible = false;
    }
  }

  disposeArrow(): void {
    if (this.ctx.arrowHelper) {
      this.ctx.arrowHelper.children.forEach((child) => {
        this.ctx.arrowHelper?.remove(child);
        if ((child as Mesh).geometry) (child as Mesh).geometry.dispose();
        if ((child as Mesh).material) {
          const mat = (child as Mesh).material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat.dispose();
        }
      });
      this.ctx.scene.remove(this.ctx.arrowHelper);
      this.ctx.arrowHelper = undefined;
    }
  }
}
